import React, { useState, useEffect } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, ArrowRight, Check, X, Play, DeviceMobile, InstagramLogo } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpsellPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showDownsell, setShowDownsell] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) verifyPayment(sessionId, 0);
  }, [sessionId]);

  const verifyPayment = async (sid, attempts) => {
    if (attempts >= 8) return;
    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      if (res.data.payment_status === 'paid') return;
      setTimeout(() => verifyPayment(sid, attempts + 1), 2000);
    } catch { setTimeout(() => verifyPayment(sid, attempts + 1), 2000); }
  };

  const handleFilmUpsell = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/funnel/upsell-checkout`, {
        products: ['film'],
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error('Checkout failed.');
    } catch { toast.error('Something went wrong.'); }
    finally { setLoading(false); }
  };

  const handleHighlightUpsell = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/funnel/upsell-checkout`, {
        products: ['highlight'],
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error('Checkout failed.');
    } catch { toast.error('Something went wrong.'); }
    finally { setLoading(false); }
  };

  const handleSkipFilm = () => setShowDownsell(true);
  const handleSkipAll = () => navigate(`/thank-you${sessionId ? `?session_id=${sessionId}` : ''}`);

  // ─── DOWNSELL: Highlight Reel (£12) ───
  if (showDownsell) {
    return (
      <div className="min-h-screen bg-[#15161B] text-white flex items-center justify-center px-6" style={{ fontFamily: "'Inter', sans-serif" }}>
        <div className="max-w-lg w-full py-16" data-testid="downsell-page">
      <ConfettiBackground />
          <div className="text-center mb-10">
            <div className="flex justify-center gap-2 mb-4">
              <InstagramLogo size={28} weight="bold" className="text-[#B89762]" />
              <DeviceMobile size={28} weight="bold" className="text-[#B89762]" />
            </div>
            <h1 className="text-3xl font-black tracking-tight mb-3" data-testid="downsell-title">
              Want something quick to share?
            </h1>
            <p className="text-gray-400 text-lg">Create a 30-second highlight reel instead</p>
          </div>

          <div className="rounded-3xl border-2 border-[#B89762]/50/40 bg-gradient-to-b from-[#B89762]/10 to-transparent p-8 mb-6" data-testid="downsell-offer">
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#B89762]/10 text-[#B89762] text-xs font-bold mb-4">SOCIAL READY</span>
              <h2 className="text-2xl font-black">Highlight Reel</h2>
              <p className="text-gray-400 text-sm mt-2">Perfect for Instagram, TikTok & sharing</p>
            </div>

            <div className="space-y-3 mb-8">
              {['20-30 second vertical video (9:16)', 'Fast-cut energy edit from your event', 'Perfect for Stories & Reels', 'Instant download & share'].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)' }}>
                    <Check size={10} weight="bold" className="text-white" />
                  </div>
                  <span className="text-gray-300">{f}</span>
                </div>
              ))}
            </div>

            <div className="text-center mb-6">
              <p className="text-4xl font-black">Only £12</p>
              <p className="text-sm text-gray-500 mt-1">one-time payment</p>
            </div>

            <button
              onClick={handleHighlightUpsell}
              disabled={loading}
              className="w-full py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)', boxShadow: '0 0 30px rgba(184,151,98,0.15)' }}
              data-testid="downsell-yes-button"
            >
              {loading ? 'Redirecting...' : 'Get Highlight Reel — £12'}
            </button>
          </div>

          <button
            onClick={handleSkipAll}
            className="w-full py-4 rounded-2xl text-base font-medium text-gray-600 hover:text-gray-400 transition-colors flex items-center justify-center gap-2"
            data-testid="downsell-no-button"
          >
            No thanks, take me to my event
          </button>
        </div>
      </div>
    );
  }

  // ─── PRIMARY: Film Upsell (£29) ───
  return (
    <div className="min-h-screen bg-[#15161B] text-white flex items-center justify-center px-6" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-xl w-full py-16" data-testid="upsell-page">
        {/* Success */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#B89762]/10 border-2 border-green-500 flex items-center justify-center">
            <Check size={28} weight="bold" className="text-[#B89762]" />
          </div>
        </div>
        <h1 className="text-2xl font-black text-center tracking-tight mb-1" data-testid="upsell-title">
          Payment Successful!
        </h1>
        <p className="text-gray-500 text-center mb-10 text-sm">Your EventDrop Capture is ready.</p>

        {/* Film Upsell */}
        <div className="rounded-3xl border-2 border-[#B89762]/50/40 bg-gradient-to-b from-[#B89762]/5 to-transparent p-8 mb-6" data-testid="upsell-offer">
          <div className="text-center mb-5">
            <p className="text-sm font-bold text-[#B89762] tracking-widest mb-3">ONE MORE THING</p>
            <h2 className="text-3xl font-black tracking-tight leading-tight" data-testid="film-upsell-headline">
              Turn your event into<br />one unforgettable film
            </h2>
          </div>

          {/* Preview video area */}
          <div className="rounded-2xl overflow-hidden mb-6 aspect-video bg-gradient-to-br from-[#15161B] to-[#0d0d0d] flex items-center justify-center relative" data-testid="film-upsell-preview">
            <FilmStrip size={80} weight="thin" className="text-white/5 absolute" />
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center border border-white/20" style={{ background: 'rgba(255,77,141,0.2)', backdropFilter: 'blur(8px)' }}>
                <Play size={20} weight="fill" className="text-white ml-0.5" />
              </div>
              <span className="text-white/60 text-sm">Preview</span>
            </div>
          </div>

          <p className="text-gray-300 text-center mb-6 leading-relaxed">
            All your guests' photos, videos & messages —<br />
            combined into one beautiful cinematic film.
          </p>

          <div className="space-y-2.5 mb-8">
            {['Automatically edited into one story', 'Ready within hours', 'Background music included', 'Relive moments you didn\'t even see'].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #A033D8, #C29D52)' }}>
                  <Check size={10} weight="bold" className="text-white" />
                </div>
                <span className="text-gray-300">{f}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-white/40 text-xs mb-4 italic">This is the moment everything comes together.</p>

          <div className="text-center mb-6">
            <p className="text-4xl font-black">Only £29</p>
            <p className="text-sm text-gray-500 mt-1">one-time payment</p>
          </div>

          <button
            onClick={handleFilmUpsell}
            disabled={loading}
            className="w-full py-5 rounded-2xl text-lg font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #A033D8 0%, #C29D52 100%)', boxShadow: '0 0 30px rgba(255,77,141,0.15)' }}
            data-testid="upsell-yes-button"
          >
            {loading ? 'Redirecting...' : 'Create My Film — £29'}
            {!loading && <ArrowRight size={20} weight="bold" />}
          </button>
        </div>

        <button
          onClick={handleSkipFilm}
          className="w-full py-4 rounded-2xl text-base font-medium text-gray-600 hover:text-gray-400 transition-colors flex items-center justify-center gap-2"
          data-testid="upsell-no-button"
        >
          No thanks
        </button>
      </div>
    </div>
  );
};

export default UpsellPage;
